import CheckIcon from "@mui/icons-material/Check";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, Button, Chip, Dialog, DialogActions, DialogTitle, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { ReactNode, useState } from "react";

import { SeloVisibilidadeAba, VisibilidadeResposta } from "./types";

/** Único arquivo do front com os textos de selo e de classe de visibilidade. */
export const TEXTO_SELO: Record<SeloVisibilidadeAba, string> = {
  SO_EMPRESA: "Só a sua empresa",
  COMPARTILHADO_COM_CORRETORA: "Compartilhado com a corretora",
};
export const NOME_ABA = { hoje: "Hoje", ativos: "Ativos e funcionários", compras: "Compras e movimentações", previsto: "O que vem aí", gente: "Minha gente" } as const;

export function SeloVisibilidade({ selo }: { selo?: SeloVisibilidadeAba | null }) {
  if (!selo || !(selo in TEXTO_SELO)) return null;
  return (
    <Chip
      size="small"
      variant="outlined"
      data-testid="selo-visibilidade"
      label={TEXTO_SELO[selo]}
      color={selo === "SO_EMPRESA" ? "primary" : "default"}
    />
  );
}

export function TituloComSelo({ titulo, aoLado }: { titulo: string; aoLado?: ReactNode }) {
  const h2 = (
    <Typography component="h2" variant="h5" sx={{ fontFamily: "Sora", fontWeight: 700 }}>
      {titulo}
    </Typography>
  );
  if (!aoLado) return h2;
  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      {h2}
      {aoLado}
    </Box>
  );
}

function SimNao({ sim }: { sim: boolean }) {
  const Icone = sim ? CheckIcon : RemoveIcon;
  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Icone aria-hidden fontSize="small" sx={{ color: sim ? "#1E7A3C" : "text.secondary" }} />
      {sim ? "Sim" : "Não"}
    </Box>
  );
}

export function QuemVeCadaAba({ abas }: { abas: VisibilidadeResposta["abas"] }) {
  const [aberto, setAberto] = useState(false);
  const linhas = abas.filter((a) => a.aba in NOME_ABA);
  const compartilha = linhas.some((a) => a.selo === "COMPARTILHADO_COM_CORRETORA");
  // Só afirma "ninguém" com selo null explícito em toda aba compartilhável; selo ausente (backend antigo ou falha) não afirma nada.
  const compartilhaveis = linhas.filter((a) => a.classe === "EMPRESA_E_STAFF");
  const ninguem = !compartilha && compartilhaveis.length > 0 && compartilhaveis.every((a) => a.selo === null);
  const rodape = compartilha
    ? "Alguém da sua corretora tem acesso à sua empresa. As abas com eventos da folha ficam só com você."
    : ninguem
      ? "Hoje ninguém da sua corretora tem acesso à sua empresa."
      : null;
  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setAberto(true)}
        sx={{ border: 0, bgcolor: "transparent", p: 0, font: "inherit", fontSize: "0.875rem", color: "primary.main", textDecoration: "underline", cursor: "pointer" }}
      >
        Quem vê cada aba
      </Box>
      <Dialog open={aberto} onClose={() => setAberto(false)} fullWidth maxWidth="sm">
        <DialogTitle>Quem vê cada aba do painel</DialogTitle>
        <Box px={3} pb={1}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Aba do painel</TableCell>
                <TableCell>Sua empresa</TableCell>
                <TableCell>Sua corretora</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {linhas.map((a) => (
                <TableRow key={a.aba} data-testid={`quem-ve-${a.aba}`}>
                  <TableCell>{NOME_ABA[a.aba as keyof typeof NOME_ABA]}</TableCell>
                  <TableCell><SimNao sim /></TableCell>
                  <TableCell><SimNao sim={a.selo === "COMPARTILHADO_COM_CORRETORA"} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {rodape && (
            <Typography variant="body2" color="text.secondary" mt={1.5} data-testid="quem-ve-rodape">
              {rodape}
            </Typography>
          )}
        </Box>
        <DialogActions>
          <Button onClick={() => setAberto(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
